import { useTranslation } from 'react-i18next';
import { useToasts } from 'react-toast-notifications';
import React, { useEffect, useState } from 'react';
import styles from './CommunicationsDetails.module.scss'
import Loading from 'common/services/Loading';
import Logger from 'common/services/Logger';
import { LOGGER_LOG_TYPE } from 'Config';
import UsersService from 'api/users/UsersService';
import RolesService from 'api/roles/RolesService';
import CommunicationsService from 'api/communications/CommunicationsService';
import { UsersSelectItemDto } from 'api/users/models/UserDto';
import CheckBox from 'common/components/checkBox/CheckBox';
import { CommunicationConcatType, CommunicationDto } from 'api/communications/models/CommunicationDto';
import { RoleDto } from 'api/roles/models/RoleDto';
import Button from 'common/components/button/Button';
import InputSearch from 'common/components/inputSearch/InputSearch';
import { useDebouncedCallback } from 'use-debounce/lib';
import { UserProfile } from 'api/account/models/UserProfile';
import { useSelector } from 'react-redux';
import { Reducers } from 'store/types';
import { FaInfoCircle } from 'react-icons/fa';
import ReactTooltip from 'react-tooltip';
import { useLocation } from 'react-router-dom';
import VehicleReservationsService from 'api/vehicleReservations/VehicleReservationsService';

type Props = {
};

const CommunicationsDetails: React.FC<Props> = ({ }: Props) => {
    const { t } = useTranslation();
    const { addToast } = useToasts();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const communicationTypeParam = queryParams.get('type');
    const [usersList, setUsersList] = useState<UsersSelectItemDto[]>([]);
    const [allUsersList, setAllUsersList] = useState<UsersSelectItemDto[]>([]);
    const [communicationsList, setCommunicationsList] = useState<CommunicationDto[]>([]);
    const [rolesList, setRolesList] = useState<RoleDto[]>([]);
    const [communication, setCommunication] = useState<CommunicationDto>();
    const [filterCommunication, setFilterCommunication] = useState<string>('');
    const [filterUser, setFilterUser] = useState<string>('');
    const [filterRole, setFilterRole] = useState<string>('');
    const [usersListFiltered, setUsersListFiltered] = useState<UsersSelectItemDto[]>([]);
    const [communicationsListFiltered, setCommunicationsListFiltered] = useState<CommunicationDto[]>([]);
    const [rolesListFiltered, setRolesListFiltered] = useState<RoleDto[]>([]);

    const userProfile = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const hasCommunicationsWritePolicy = UsersService.hasPolicies(userProfile?.policies || [], ['SETTINGUP_COMMUNICATIONS_WRITE']);

    const PENDING_APPROVAL_TYPES = ['RESERVATION_PENDING_APPROVAL_ALERT', 'RESERVATION_PENDING_APPROVAL_EMAIL'];
    const APPROVED_REJECTED_TYPES = ['RESERVATION_APPROVED_ALERT', 'RESERVATION_APPROVED_EMAIL', 'RESERVATION_REJECTED_ALERT', 'RESERVATION_REJECTED_EMAIL'];
    const AUTO_NOTIFICATION_TYPES = [...PENDING_APPROVAL_TYPES, ...APPROVED_REJECTED_TYPES];

    const isAutoNotificationType = communication?.name ? AUTO_NOTIFICATION_TYPES.includes(communication.name) : false;
    const isPendingApprovalType = communication?.name ? PENDING_APPROVAL_TYPES.includes(communication.name) : false;

    const getData = async () => {
        try {
            Loading.show();
            const users = await UsersService.getAllForCompany();
            setAllUsersList(users);
            setUsersList(users);
            const roles = await RolesService.getAllForCompany();
            setRolesList(roles);
            const communications = await CommunicationsService.getAllForCompany();

            setCommunicationsList(communications);
            if (communications.length >= 1) {
                // If there's a type parameter in URL, try to select that communication
                let selectedComm = communications[0];
                if (communicationTypeParam) {
                    const matchedComm = communications.find(c => c.name?.startsWith(communicationTypeParam));
                    if (matchedComm) {
                        selectedComm = matchedComm;
                    }
                }
                onCommunicationSelected(selectedComm, users, roles);
            }
            Loading.hide();

        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't get roles list`, error);
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
            Loading.hide();
        }
    };

    useEffect(() => {
        getData();
    }, []);

    const roleName = (role: RoleDto): string => {
        if (role.system === true || role.readOnly === true) {
            return t(('common.roles.' + role.name) as any)
        }
        else {
            return role.name;
        }
    }

    const onCommunicationSelected = async (communication: CommunicationDto, users: UsersSelectItemDto[], roles: RoleDto[]) => {
        setCommunication({ ...communication });

        const isSmsType = communication.name?.endsWith('_SMS');
        const filteredUsers = isSmsType
            ? users.filter(u => u.phoneNumber && u.phoneNumber.trim() !== '')
            : users;

        const pendingTypes = ['RESERVATION_PENDING_APPROVAL_ALERT', 'RESERVATION_PENDING_APPROVAL_EMAIL'];
        if (communication.name && pendingTypes.includes(communication.name)) {
            try {
                const approvers = await VehicleReservationsService.getApprovers();
                const approverIds = (approvers || []).map(a => a.id);
                filteredUsers.forEach(u => { u.checked = approverIds.includes(u.id); });
                roles.forEach(r => { r.checked = false; });
                setUsersList([...filteredUsers]);
                setRolesList([...roles]);
                return;
            } catch {
                // approvers fetch failed, continue with default behavior
            }
        }

        setUsersList(filteredUsers);
        parseCommunicationFromExpression(communication, filteredUsers, roles);
    }

    const parseCommunicationFromExpression = async (communication: CommunicationDto, users: UsersSelectItemDto[], roles: RoleDto[]) => {
        const andOrRegex = /\((.*)\)(AND|OR)\((.*)\)/gi;
        const andOrMatch = andOrRegex.exec(communication?.expression);
        if (andOrMatch) {
            // AND / OR
            const andOr = andOrMatch[2];
            if (andOr.toUpperCase() === CommunicationConcatType.AND) {
                communication.concatType = CommunicationConcatType.AND;
            } else if (andOr.toUpperCase() === CommunicationConcatType.OR) {
                communication.concatType = CommunicationConcatType.OR;
            }
        } else {
            // Check for suffix marker |AND or |OR when only users or only roles are selected
            const suffixRegex = /\|(AND|OR)$/i;
            const suffixMatch = suffixRegex.exec(communication?.expression);
            if (suffixMatch) {
                const andOr = suffixMatch[1];
                if (andOr.toUpperCase() === CommunicationConcatType.AND) {
                    communication.concatType = CommunicationConcatType.AND;
                } else if (andOr.toUpperCase() === CommunicationConcatType.OR) {
                    communication.concatType = CommunicationConcatType.OR;
                }
            }
        }

        setCommunication({ ...communication });

        // Remove suffix marker before parsing users and roles
        const expressionWithoutSuffix = communication.expression?.replace(/\|(AND|OR)$/i, '');

        // Users
        users.forEach(x => x.checked = false);

        const usersExpression = andOrMatch ? andOrMatch[1] : expressionWithoutSuffix?.replace(/\(/g, '').replace(/\)/g, '');
        const usersRegex = /I:([^\s]+)/gi;
        const usersMatchArray = [];
        let usersMatch = usersRegex.exec(usersExpression);
        while (usersMatch != null) {
            usersMatchArray.push(usersMatch[1]);
            usersMatch = usersRegex.exec(usersExpression);
        }

        if (usersMatchArray && usersMatchArray.length > 0) {
            if (usersMatchArray.length === 1 && usersMatchArray[0] === '*') {
                if (filterUser === '') {
                    users.forEach((e: UsersSelectItemDto) => {
                        e.checked = true;
                    });
                } else {
                    users.filter(c => usersListFiltered?.find(e => e.id === c.id) != null).forEach((e: UsersSelectItemDto) => {
                        e.checked = true;
                    });
                }
            } else {
                usersMatchArray.forEach(userName => {
                    const user = users.find(u => u.userName === userName);
                    if (user) {
                        user.checked = true;
                    }
                });
            }
        }
        setUsersList([...users]);

        // Roles
        roles.forEach(x => x.checked = false);

        const rolesExpression = andOrMatch ? andOrMatch[3] : expressionWithoutSuffix?.replace(/\(/g, '').replace(/\)/g, '');
        const rolesRegex = /R:([^\s]+)/gi;

        const rolesMatchArray = [];
        let rolesMatch = rolesRegex.exec(rolesExpression);
        while (rolesMatch != null) {
            rolesMatchArray.push(rolesMatch[1]);
            rolesMatch = rolesRegex.exec(rolesExpression);
        }

        if (rolesMatchArray && rolesMatchArray.length > 0) {
            if (rolesMatchArray.length === 1 && rolesMatchArray[0] === '*') {

                if (filterRole === '') {
                    roles.forEach((r: RoleDto) => {
                        r.checked = true;
                    });
                } else {
                    roles.filter(c => rolesListFiltered?.find(e => e.id === c.id) != null).forEach((r: RoleDto) => {
                        r.checked = true;
                    });
                }

            } else {
                rolesMatchArray.forEach(userRole => {
                    const role = roles.find(r => r.id === userRole);
                    if (role) {
                        role.checked = true;
                    }
                });
            }
        }

        setRolesList([...roles]);
    }

    const onSave = async () => {
        try {
            Loading.show();

            if (communicationsList != null) {
                await CommunicationsService.update(communicationsList);
            }
            Loading.hide();
            addToast(t('common.messages.record_save_success'), {
                appearance: 'success',
            });
        } catch (error) {
            addToast(t('common.messages.record_save_error'), {
                appearance: 'error',
            });
            Logger.error(
                LOGGER_LOG_TYPE.REQUEST,
                `Couldn't create or update communication`,
                error
            );
            Loading.hide();
        }
    };

    const createExpression = (communication: CommunicationDto) => {
        // Users
        const users = usersList.filter((e: UsersSelectItemDto) => {
            return e.checked === true;
        });
        const usersExpression = users.map((u: UsersSelectItemDto) => {
            return 'I:' + u.userName;
        });
        const usersString = isAllUsersChecked(false) ? '(I:*)' : usersExpression && usersExpression.length > 0 ? '(' + usersExpression.join(' OR ') + ')' : '';

        // Roles
        const roles = rolesList.filter((r: RoleDto) => {
            return r.checked === true;
        });
        const rolesExpression = roles ? roles.map((r: RoleDto) => {
            return 'R:' + r.id;
        }) : [];

        const rolesString = isAllRolesChecked(false) ? '(R:*)' : rolesExpression && rolesExpression.length > 0 ? '(' + rolesExpression.join(' OR ') + ')' : '';
        const andOr = communication.concatType && communication.concatType === CommunicationConcatType.OR ? CommunicationConcatType.OR : CommunicationConcatType.AND;

        // Result
        if (usersString && rolesString) {
            communication.expression = usersString + andOr + rolesString;
        } else if (usersString && !rolesString) {
            // Add suffix to preserve concatType when only users are selected
            communication.expression = usersString + '|' + andOr;
        } else if (!usersString && rolesString) {
            // Add suffix to preserve concatType when only roles are selected
            communication.expression = rolesString + '|' + andOr;
        } else {
            // Preserve concatType even when no users or roles are selected
            communication.expression = '|' + andOr;
        }

        setCommunicationsList([
            ...communicationsList.map(r => {
                if (r.id === communication.id) {
                    r.expression = communication.expression;
                    r.concatType = communication.concatType;
                }
                return r;
            }),
        ]);
    }

    const debouncedCommunication = useDebouncedCallback((value: string) => {
        setFilterCommunication(value);
        const communications = communicationsList.filter(item => {
            return t(('communications.types.' + item.name) as any).removeAccents().toLowerCase().indexOf(value.removeAccents().toLowerCase()) !== -1;
        });
        setCommunicationsListFiltered(communications);
    }, 500);

    const debouncedUser = useDebouncedCallback((value: string) => {
        setFilterUser(value);
        const users = usersList.filter(item => {
            return item.realName.removeAccents().toLowerCase().indexOf(value.removeAccents().toLowerCase()) !== -1
                || item.userName.removeAccents().toLowerCase().indexOf(value.removeAccents().toLowerCase()) !== -1
        });
        setUsersListFiltered(users);
    }, 500);

    const debouncedRole = useDebouncedCallback((value: string) => {
        setFilterRole(value);
        const roles = rolesList.filter(item => {
            return item.name.removeAccents().toLowerCase().indexOf(value.removeAccents().toLowerCase()) !== -1
        });
        setRolesListFiltered(roles);
    }, 500);

    const setAnd = () => {
        if (communication) {
            communication.concatType = CommunicationConcatType.AND;
            setCommunication(communication);
            createExpression(communication);
            setCommunication({ ...communication });
        }
    }

    const setOr = () => {
        if (communication) {
            communication.concatType = CommunicationConcatType.OR;
            setCommunication(communication);
            createExpression(communication);
            setCommunication({ ...communication });
        }
    }

    const isAllUsersChecked = (checkFiltered: boolean) => {
        if (checkFiltered) {
            return filterUser != '' && usersListFiltered.length > 0 && usersListFiltered?.every(_ => _.checked);
        }
        return usersList?.length > 0 && usersList?.every(_ => _.checked);
    }

    const onAllUsersChecked = (checked: boolean) => {
        selectOrUnSelectUsers(checked, filterUser != '');
        if (communication) {
            createExpression(communication);
        }
    }

    const selectOrUnSelectUsers = async (checked: boolean, filter: boolean) => {
        if (!filter) {
            usersList.forEach((e: UsersSelectItemDto) => {
                e.checked = checked;
            });

        } else {
            usersList.filter(c => usersListFiltered?.find(e => e.id === c.id) != null).forEach((e: UsersSelectItemDto) => {
                e.checked = checked;
            });
        }
        setUsersList([...usersList]);
    }

    const isAllRolesChecked = (checkFiltered: boolean) => {
        if (checkFiltered) {
            return filterRole != '' && rolesListFiltered.length > 0 && rolesListFiltered?.every(_ => _.checked);
        }
        return rolesList?.length > 0 && rolesList?.every(_ => _.checked);
    }

    const onAllRolesChecked = (checked: boolean) => {
        selectOrUnSelectRoles(checked, filterRole != '');
        if (communication) {
            createExpression(communication);
        }
    }

    const selectOrUnSelectRoles = (checked: boolean, filter: boolean) => {
        if (!filter) {
            rolesList.forEach((r: RoleDto) => {
                r.checked = checked;
            });
        } else {
            rolesList.filter(c => rolesListFiltered?.find(e => e.id === c.id) != null).forEach((r: RoleDto) => {
                r.checked = checked;
            });
        }
        setRolesList([...rolesList]);
    }

    return (
        <div className={styles.container}>
            {communication &&
                (communication.name == 'EXPENSE_TREATED_ALERT'
                    || communication.name == 'EXPENSE_TREATED_EMAIL'
                    || communication.name == 'EXPENSE_NOT_TREATED_ALERT'
                    || communication.name == 'EXPENSE_NOT_TREATED_EMAIL')
                && <span className={styles.infoMessage}>
                    {t('communications.message.alert_charges')}
                </span>}
            {communication &&
                (communication.name == 'MAINTENANCE_PLANNED_ALERT'
                    || communication.name == 'MAINTENANCE_PLANNED_EMAIL')
                && <span className={styles.infoMessage}>
                    {t('communications.message.alert_maintenances')}
                </span>}
            {communication &&
                (communication.name == 'EXPENSE_REFUND_APPROVED_ALERT'
                    || communication.name == 'EXPENSE_REFUND_APPROVED_EMAIL'
                    || communication.name == 'EXPENSE_REFUND_REFUSED_ALERT'
                    || communication.name == 'EXPENSE_REFUND_REFUSED_EMAIL')
                && <span className={styles.infoMessage}>
                    {t('communications.message.alert_charges_refund')}
                </span>}
            {isPendingApprovalType && <span className={styles.infoMessage}>
                {t('communications.message.alert_reservation_pending_approval')}
            </span>}
            {communication && !isPendingApprovalType && APPROVED_REJECTED_TYPES.includes(communication.name || '') && <span className={styles.infoMessage}>
                {t('communications.message.alert_reservation_approved_rejected')}
            </span>}

            <div className={styles.configContainer}>
                <div className={styles.communicationsContainer}>
                    <div className={styles.searchContainer}>
                        <InputSearch inputContainerClassName={styles.search} onChangeValue={debouncedCommunication} placeholder={t('communications.search_placeholder_comunication')}></InputSearch>
                    </div>
                    <div className={styles.tableHeader}>
                        <span className={styles.tableHeaderTitle}>{t('communications.title')}</span>
                    </div>
                    <div className={styles.scrollContainer}>
                        <table className={styles.table}>
                            <tbody>
                                {(filterCommunication != '' ? communicationsListFiltered : communicationsList).map((row, rowIndex) => (
                                    <tr key={`row-r-${rowIndex}`} className={styles.rowHover} onClick={() => onCommunicationSelected(row, allUsersList, rolesList)}>
                                        <td className={`${styles.bodyColumn} ${row.id === communication?.id ? styles.striped : ''}`}>
                                            <div className={styles.communicationContainer}>
                                                <div className={styles.communicationInfo}>{t(('communications.types.' + row.name) as any)}</div>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
                { /*##################  Users ################## */}
                    <div className={styles.usersContainer}>
                        <div className={styles.searchContainer}>
                            <InputSearch inputContainerClassName={styles.search} onChangeValue={debouncedUser} placeholder={t('communications.search_placeholder_users')}></InputSearch>
                        </div>
                        <div className={styles.tableHeader}>
                            <span className={styles.tableHeaderTitle}>
                                {t('users.title')}
                                {communication?.name?.endsWith('_SMS') && (
                                    <>
                                        <FaInfoCircle data-tip={t('communications.sms_users_filter_info')} data-for="smsUsersFilterTooltip" style={{ marginLeft: '0.5rem', cursor: 'pointer' }} />
                                        <ReactTooltip id="smsUsersFilterTooltip" />
                                    </>
                                )}
                            </span>
                            <CheckBox checked={isAllUsersChecked(filterUser != '')} onChange={e => { onAllUsersChecked(e.target.checked) }} disabled={isAutoNotificationType || !hasCommunicationsWritePolicy} />
                        </div>
                        <div className={styles.scrollContainer}>
                            <table className={styles.table}>
                                <tbody>
                                    {(filterUser != '' ? usersListFiltered : usersList).map((row, rowIndex) => (
                                        <tr key={`row-u-${rowIndex}`} className={styles.rowHover}>
                                            <td className={styles.bodyColumn}>
                                                <div className={styles.userContainer}>
                                                    <div className={styles.userInfo}>
                                                        <span className={styles.name}>{row.realName}</span>
                                                        <span className={styles.email}>({communication?.name?.endsWith('_SMS') && row.phoneNumber ? `${row.phoneNumber} - ` : ''}{row.userName})</span>
                                                    </div>
                                                    <div className={styles.checkbox}>
                                                        <CheckBox
                                                            onChange={e => {
                                                                setUsersList([
                                                                    ...usersList.map(r => {
                                                                        if (r.id === row.id) {
                                                                            r.checked = e.target.checked;
                                                                        }
                                                                        return r;
                                                                    }),
                                                                ]);
                                                                if (communication) {
                                                                    createExpression(communication);
                                                                }
                                                            }}
                                                            checked={row.checked || false}
                                                            key={row.id}
                                                            disabled={isAutoNotificationType || !hasCommunicationsWritePolicy}
                                                        />
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div className={styles.operationsContainer}>
                        <div className={styles.multiButton}>
                            <Button text={t('common.and')}
                                className={styles.firstChild}
                                preset={communication == null || communication?.concatType != 'OR' ? 'primary' : 'secondary'}
                                onClick={setAnd}
                                size={'small'}
                                disabled={isAutoNotificationType}
                            />
                            <Button text={t('common.or')}
                                className={styles.lastChild}
                                preset={!(communication == null || communication?.concatType != 'OR') ? 'primary' : 'secondary'}
                                onClick={setOr}
                                size={'small'}
                                disabled={isAutoNotificationType}
                            />
                        </div>
                    </div>
                    { /*##################  Roles ################## */}
                    <div className={styles.rolesContainer}>

                        <div className={styles.searchContainer}>
                            <InputSearch inputContainerClassName={styles.search} onChangeValue={debouncedRole} placeholder={t('communications.search_placeholder_roles')}></InputSearch>
                        </div>

                        <div className={styles.tableHeader}>
                            <span className={styles.tableHeaderTitle}>{t('roles.title')}</span>
                            <CheckBox checked={isAllRolesChecked(filterRole != '')} onChange={e => { onAllRolesChecked(e.target.checked) }} disabled={isAutoNotificationType || !hasCommunicationsWritePolicy} />
                        </div>

                        <div className={styles.scrollContainer}>
                            <table className={styles.table}>
                                <tbody>
                                    {(filterRole != '' ? rolesListFiltered : rolesList).map((row, rowIndex) => (
                                        <tr key={`row-u-${rowIndex}`} className={styles.rowHover}>
                                            <td className={styles.bodyColumn}>
                                                <div className={styles.roleContainer}>
                                                    <div className={styles.roleInfo}>
                                                        {roleName(row)}
                                                    </div>
                                                    <div className={styles.checkbox}>
                                                        <CheckBox
                                                            onChange={e => {
                                                                setRolesList([
                                                                    ...rolesList.map(r => {
                                                                        if (r.id === row.id) {
                                                                            r.checked = e.target.checked;
                                                                        }
                                                                        return r;
                                                                    }),
                                                                ]);
                                                                if (communication) {
                                                                    createExpression(communication);
                                                                }
                                                            }}
                                                            checked={row.checked || false}
                                                            key={row.id}
                                                            disabled={isAutoNotificationType || !hasCommunicationsWritePolicy}
                                                        />
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
            </div>
            {/* <div className={styles.expressionContainer}>
                <InputResizable disabled={true} value={communication?.expression}></InputResizable>
            </div> */}
            <div className={styles.buttonContainer}>
                {hasCommunicationsWritePolicy && !isAutoNotificationType && <Button type='button' text={t('common.save')} onClick={() => onSave()} />}
            </div>
        </div>
    );
};

export default CommunicationsDetails;
